<template>
  <b-card class="mx-auto reset-card" style="max-width: 640px; margin: 50px">
    <div class="text-center mb-4">
      <h3 class="mb-2">Reinitialisation du mot de passe</h3>
      <p class="text-muted mb-0">
        {{ hasCode ? "Saisissez votre nouveau mot de passe." : "Entrez votre adresse e-mail pour recevoir un lien de reinitialisation." }}
      </p>
    </div>

    <b-alert v-if="successMessage" variant="success" show class="mb-3">
      {{ successMessage }}
    </b-alert>

    <b-alert v-if="errorMessage" variant="danger" show class="mb-3">
      {{ errorMessage }}
    </b-alert>

    <b-form v-if="!hasCode" @submit.prevent="handleRequestReset">
      <b-form-group label="Adresse e-mail" label-for="reset-email">
        <b-form-input
          id="reset-email"
          v-model.trim="requestForm.email"
          type="email"
          required
          placeholder="ex: jean@example.com"
        />
      </b-form-group>

      <b-button type="submit" variant="primary" block :disabled="isSubmitting">
        {{ isSubmitting ? "Envoi..." : "Envoyer le lien" }}
      </b-button>
    </b-form>

    <b-form v-else @submit.prevent="handleResetPassword">
      <b-form-group label="Nouveau mot de passe" label-for="new-password">
        <b-form-input
          id="new-password"
          v-model="resetForm.password"
          type="password"
          :state="passwordState"
          minlength="8"
          maxlength="150"
          required
          placeholder="Nouveau mot de passe"
        />
        <b-form-invalid-feedback :state="passwordState">
          Le mot de passe doit contenir entre 8 et 150 caracteres.
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Confirmer le mot de passe" label-for="confirm-password">
        <b-form-input
          id="confirm-password"
          v-model="resetForm.confirmPassword"
          type="password"
          :state="confirmPasswordState"
          minlength="8"
          maxlength="150"
          required
          placeholder="Confirmez le mot de passe"
        />
        <b-form-invalid-feedback :state="confirmPasswordState">
          Les mots de passe ne correspondent pas.
        </b-form-invalid-feedback>
        <b-form-valid-feedback :state="confirmPasswordState">
          Les mots de passe correspondent.
        </b-form-valid-feedback>
      </b-form-group>

      <b-button type="submit" variant="primary" block :disabled="isSubmitting || !canSubmitReset">
        {{ isSubmitting ? "Validation..." : "Mettre a jour le mot de passe" }}
      </b-button>
    </b-form>
  </b-card>
</template>

<script>
import UserService from "@/services/user.service";

export default {
  name: "ResetPassword",
  data() {
    return {
      isSubmitting: false,
      successMessage: "",
      errorMessage: "",
      requestForm: {
        email: ""
      },
      resetForm: {
        password: "",
        confirmPassword: ""
      }
    };
  },
  computed: {
    resetCode() {
      return this.$route.query.code;
    },
    hasCode() {
      return typeof this.resetCode === "string" && this.resetCode.length > 0;
    },
    isPasswordLengthValid() {
      const length = (this.resetForm.password || "").length;
      return length >= 8 && length <= 150;
    },
    passwordsMatch() {
      return this.resetForm.password === this.resetForm.confirmPassword;
    },
    passwordState() {
      if (!this.resetForm.password) return null;
      return this.isPasswordLengthValid;
    },
    confirmPasswordState() {
      if (!this.resetForm.confirmPassword) return null;
      if (!this.isPasswordLengthValid) return false;
      return this.passwordsMatch;
    },
    canSubmitReset() {
      return this.isPasswordLengthValid && this.passwordsMatch;
    }
  },
  methods: {
    clearFeedback() {
      this.successMessage = "";
      this.errorMessage = "";
    },
    async handleRequestReset() {
      this.clearFeedback();
      this.isSubmitting = true;

      try {
        const res = await UserService.requestPasswordReset(this.requestForm.email);
        if (res.status === 200 && res.data.success === true) {
          this.successMessage = "Si un compte existe avec cette adresse e-mail, un lien de reinitialisation a ete envoye.";
          this.requestForm.email = "";
        } else {
          this.errorMessage = "Impossible d'envoyer le lien de reinitialisation.";
        }
      } catch (error) {
        this.errorMessage = error.message || "Impossible d'envoyer le lien de reinitialisation.";
      } finally {
        this.isSubmitting = false;
      }
    },
    async handleResetPassword() {
      this.clearFeedback();

      if (!this.isPasswordLengthValid) {
        this.errorMessage = "Le mot de passe doit contenir entre 8 et 150 caracteres.";
        return;
      }

      if (this.resetForm.password !== this.resetForm.confirmPassword) {
        this.errorMessage = "Les mots de passe ne correspondent pas.";
        return;
      }

      this.isSubmitting = true;
      try {
        const res = await UserService.resetPassword(this.resetCode, this.resetForm.password);
        if (res.status === 200 && res.data.success === true) {
          this.successMessage = "Mot de passe mis a jour. Vous pouvez maintenant vous connecter.";
          this.resetForm.password = "";
          this.resetForm.confirmPassword = "";

          setTimeout(() => {
            this.$router.push({ path: "/auth/login", query: { login: "required" } });
          }, 3000);
        } else {
          this.errorMessage = "Code de reinitialisation invalide ou expire.";
        }
      } catch (error) {
        this.errorMessage = error.message || "Erreur lors de la reinitialisation du mot de passe.";
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>
.reset-card {
  border: 1px solid #e5e7eb;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}
</style>
